/// <reference path="../../css.d.ts" />
"use client";
// @ubm/ui/components/liquid-tab-bar — implémentation MAISON (lot U12) d'après la documentation et l'aperçu PUBLICS de
// « Liquid tab bar » (uiarc.dev, Arc Pro). Source Pro originale NON reçue, rien n'en est copié. Provenance : README.md.
//
// Moteur : les noms publics Root / List / Trigger de `@ubm/ui/components/tabs` (Tabs/Radix reçu) portent rôles, roving
// tabindex, flèches, Début/Fin, sélection et sémantique contrôlée. Ce fichier n'ajoute AUCUN second moteur clavier/ARIA/focus :
// il dessine la lentille (bords menant / traînant, étirement, amincissement, penchée au survol, lancer) et la copie inversée
// décorative (aria-hidden, inerte, sans élément focalisable). Un glisser ou un lancer n'émet qu'UN `onValueChange` quand la
// cible diffère ; un parent qui refuse garde sa valeur réelle et la lentille y revient. Une valeur qui ne correspond à aucun
// onglet masque la lentille : aucun onglet n'est annoncé sélectionné à tort.
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { animate, motion, motionValue, useMotionValue, useReducedMotionConfig as useReducedMotion, useTransform, type MotionValue } from "motion/react";
import { Root as TabsRoot, List as TabsList, Trigger as TabsTrigger } from "../tabs";
import { AnimatedCounter } from "../animated-counter";
import { useOverlayHostEngaged } from "../../overlays/OverlayHost";
import { motionTokens } from "../../lib/motion-tokens";
import "../../styles/foundation.css";
import styles from "./liquid-tab-bar.module.css";

export interface LiquidTab {
  value: string;
  label: string;
  icon: ReactNode;
  /** Dessiné dans la lentille ; défaut : `icon`, rempli par la feuille de style. */
  activeIcon?: ReactNode;
  /** Compte roulant ; 0 ou absent : pas de pastille. */
  badge?: number;
  /** Ce que les lecteurs d'écran entendent avec la pastille (défaut : le nombre). */
  badgeLabel?: string;
}

export interface LiquidTabBarProps {
  tabs: LiquidTab[];
  value: string;
  onValueChange: (value: string) => void;
  /** Libellés visibles : l'onglet choisi, tous, ou aucun. */
  labels?: "active" | "always" | "none";
  /** Nom accessible de la liste d'onglets. */
  label?: string;
  /** Base des ids d'onglet et de panneau (`liquidTabId` / `liquidPanelId`). Défaut : id généré. */
  id?: string;
  className?: string;
}

/** Id de l'onglet `value` pour la base `id` (même valeur que l'attribut posé sur le vrai onglet). */
export const liquidTabId = (id: string, value: string) => `${id}-tab-${value}`;
/** Id du panneau que l'onglet `value` désigne par `aria-controls`. */
export const liquidPanelId = (id: string, value: string) => `${id}-panel-${value}`;

const SLOT = 44;
const GAP = 2;
const PAD = 4;
const LABEL_EXTRA = 8;
const DRAG_START = 4;
const LEAD = { type: "spring" as const, stiffness: 620, damping: 42 };
const TRAIL = { type: "spring" as const, stiffness: 240, damping: 28 };

type Layout = { lefts: number[]; widths: number[]; total: number };

/** Résistance au-delà d'une borne : de moins en moins, jamais plus de `limit` px. */
const rubber = (distance: number, limit = 18) => Math.sign(distance) * (1 - 1 / ((Math.abs(distance) * 0.55) / limit + 1)) * limit;

/** Géométrie pure de la barre : position et largeur de chaque onglet (fente 44px + libellé ouvert). */
export function liquidLayout(count: number, open: boolean[], labelWidths: number[], slot = SLOT): Layout {
  const lefts: number[] = [];
  const widths: number[] = [];
  let x = PAD;
  for (let index = 0; index < count; index += 1) {
    const width = slot + (open[index] ? (labelWidths[index] ?? 0) + LABEL_EXTRA : 0);
    lefts.push(x);
    widths.push(width);
    x += width + GAP;
  }
  return { lefts, widths, total: count ? x - GAP + PAD : PAD * 2 };
}

/** Le lancer : où la lentille se pose, à partir de la position projetée ; un lancer franc avance d'au moins un onglet. */
export function liquidLanding(lefts: number[], selected: number, projected: number, velocity: number): number {
  let best = selected;
  let bestDistance = Infinity;
  lefts.forEach((x, index) => { const d = Math.abs(x - projected); if (d < bestDistance) { bestDistance = d; best = index; } });
  if (best === selected && Math.abs(velocity) > 0.35) best = Math.min(lefts.length - 1, Math.max(0, selected + Math.sign(velocity)));
  return best;
}

function accessibleName(tab: LiquidTab) {
  if (!tab.badge) return tab.label;
  return `${tab.label}, ${tab.badgeLabel ?? String(tab.badge)}`;
}

function LabelSpan({ width, children }: { width: MotionValue<number>; children: ReactNode }) {
  return <motion.span className={styles.label} style={{ width }}><span className={styles.labelText}>{children}</span></motion.span>;
}

export function LiquidTabBar({ tabs, value, onValueChange, labels = "active", label = "Sections", id, className }: LiquidTabBarProps) {
  const generated = useId();
  const sizingRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(336);
  useLayoutEffect(() => {
    const node = sizingRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const read = () => setAvailableWidth(node.clientWidth);
    read(); const observer = new ResizeObserver(read); observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const baseId = id ?? `liquid${generated.replace(/:/g, "")}`;
  const reduced = useReducedMotion() ?? false;
  const engaged = useOverlayHostEngaged();
  const selected = tabs.findIndex(tab => tab.value === value);

  // Largeurs naturelles des libellés, mesurées sur des copies cachées quand les onglets changent.
  const measureRef = useRef<HTMLSpanElement>(null);
  const [labelWidths, setLabelWidths] = useState<number[]>([]);
  const labelKey = tabs.map(tab => tab.label).join("\u0000");
  useLayoutEffect(() => {
    const node = measureRef.current;
    if (!node) return;
    const read = () => {
      const next = Array.from(node.children).map(child => Math.ceil((child as HTMLElement).offsetWidth));
      setLabelWidths(previous => previous.length === next.length && previous.every((w, i) => w === next[i]) ? previous : next);
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, [labelKey]);

  const open = tabs.map((_, index) => labels === "always" || (labels === "active" && index === selected));
  const compactSlot = Math.max(SLOT, Math.min(84, (availableWidth - PAD * 2 - GAP * Math.max(0, tabs.length - 1)) / Math.max(1, tabs.length)));
  const slot = labels === "always" ? compactSlot : SLOT;
  const layout = liquidLayout(tabs.length, labels === "always" ? tabs.map(() => false) : open, labelWidths, slot);

  // Largeur animée de chaque libellé : PARTAGÉE par l'onglet réel et sa copie dans la lentille, donc toujours alignés.
  const widthValues = useRef<MotionValue<number>[]>([]);
  const widthTargets = useRef<number[]>([]);
  const widthOf = (index: number) => (widthValues.current[index] ??= motionValue(0));
  const widthKey = tabs.map((_, index) => (labels === "always" ? compactSlot - 8 : open[index] ? (labelWidths[index] ?? 0) + LABEL_EXTRA : 0)).join(",");
  useLayoutEffect(() => {
    widthKey.split(",").forEach((raw, index) => {
      const target = Number(raw);
      if (widthTargets.current[index] === target && !reduced) return;
      const first = widthTargets.current[index] === undefined;
      widthTargets.current[index] = target;
      const width = widthOf(index);
      if (reduced || first) width.jump(target);
      else animate(width, target, motionTokens.spring.morph);
    });
  }, [widthKey, reduced]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── La lentille : deux bords, menant (rapide) et traînant (lent) ──
  const startLeft = selected >= 0 ? layout.lefts[selected] ?? PAD : PAD;
  const left = useMotionValue(startLeft);
  const right = useMotionValue(startLeft + (selected >= 0 ? layout.widths[selected] ?? SLOT : SLOT));
  const lean = useMotionValue(0);
  const [dragging, setDragging] = useState(false);
  useEffect(() => { if (reduced) lean.jump(0); }, [reduced, lean]);
  const placedOnce = useRef(false);
  const targetLeft = selected >= 0 ? layout.lefts[selected] ?? PAD : null;
  const targetWidth = selected >= 0 ? layout.widths[selected] ?? SLOT : SLOT;

  useLayoutEffect(() => {
    if (targetLeft === null || dragging) return;
    const targetRight = targetLeft + targetWidth;
    if (reduced || !placedOnce.current) {
      placedOnce.current = true;
      left.jump(targetLeft);
      right.jump(targetRight);
      return;
    }
    const goingRight = targetLeft > left.get();
    const a = animate(left, targetLeft, goingRight ? TRAIL : LEAD);
    const b = animate(right, targetRight, goingRight ? LEAD : TRAIL);
    return () => { a.stop(); b.stop(); };
  }, [targetLeft, targetWidth, dragging, reduced, left, right]);

  // Plus la lentille s'étire, plus elle s'amincit.
  const clipPath = useTransform(() => {
    const l = left.get() + lean.get();
    const r = right.get() + lean.get();
    const stretch = Math.min(7, Math.max(0, ((r - l) / targetWidth - 1) * 6));
    const inset = PAD + stretch;
    return `inset(${inset.toFixed(2)}px calc(100% - ${r.toFixed(2)}px) ${inset.toFixed(2)}px ${Math.max(0, l).toFixed(2)}px round 999px)`;
  });

  // Penchée vers l'onglet survolé (souris seulement, jamais au toucher).
  function onPointerOver(event: ReactPointerEvent) {
    if (event.pointerType !== "mouse" || reduced || dragging || selected < 0) return;
    const tab = (event.target as HTMLElement).closest<HTMLElement>("[data-tab-index]");
    const index = tab ? Number(tab.dataset.tabIndex) : selected;
    animate(lean, Math.sign(index - selected) * 4, motionTokens.spring.snappy);
  }
  function onPointerLeave() {
    if (reduced) lean.jump(0); else animate(lean, 0, motionTokens.spring.snappy);
  }

  // ── Glisser / lancer la lentille depuis l'onglet choisi ──
  const drag = useRef<{ pointer: number; startX: number; grab: number; moved: boolean; list: HTMLElement; samples: { t: number; x: number }[] } | null>(null);
  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (!engaged || event.button !== 0 || !event.isPrimary || selected < 0) return;
    const tab = (event.target as HTMLElement).closest<HTMLElement>("[data-tab-index]");
    if (!tab || Number(tab.dataset.tabIndex) !== selected) return;
    const list = event.currentTarget;
    const rect = list.getBoundingClientRect();
    drag.current = { pointer: event.pointerId, startX: event.clientX, grab: event.clientX - rect.left - left.get(), moved: false, list, samples: [{ t: event.timeStamp, x: left.get() }] };
  }
  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const state = drag.current;
    if (!state || state.pointer !== event.pointerId) return;
    if (!state.moved) {
      if (Math.abs(event.clientX - state.startX) < DRAG_START) return;
      state.moved = true;
      setDragging(true);
      state.list.setPointerCapture?.(event.pointerId);
    }
    const rect = state.list.getBoundingClientRect();
    const min = layout.lefts[0] ?? PAD;
    const max = layout.lefts[tabs.length - 1] ?? PAD;
    const raw = event.clientX - rect.left - state.grab;
    const edge = Math.min(max, Math.max(min, raw));
    const placed = reduced ? edge : edge + rubber(raw - edge);
    left.set(placed);
    right.set(placed + targetWidth);
    state.samples.push({ t: event.timeStamp, x: placed });
    if (state.samples.length > 6) state.samples.shift();
  }
  function finish(event: ReactPointerEvent<HTMLDivElement>, cancelled: boolean) {
    const state = drag.current;
    if (!state || state.pointer !== event.pointerId) return;
    drag.current = null;
    if (!state.moved) return;
    let chosen = selected;
    if (!cancelled && engaged) {
      const first = state.samples[0]!;
      const last = state.samples[state.samples.length - 1]!;
      const elapsed = last.t - first.t;
      const velocity = elapsed > 8 && event.timeStamp - last.t < 80 ? (last.x - first.x) / elapsed : 0;
      chosen = liquidLanding(layout.lefts, selected, left.get() + velocity * 160, velocity);
    }
    setDragging(false);
    const next = tabs[chosen];
    if (next && chosen !== selected) onValueChange(next.value);
  }

  // Hôte désengagé pendant un geste : le geste est abandonné sans rappel, la lentille rejoint la valeur réelle.
  useEffect(() => { if (!engaged && drag.current) { drag.current = null; setDragging(false); } }, [engaged]);

  return <div ref={sizingRef} className={styles.sizing} data-labels={labels}><TabsRoot value={value} onValueChange={onValueChange} activationMode="automatic" className={[styles.root, className].filter(Boolean).join(" ")}>
    <TabsList aria-label={label} className={styles.list} data-labels={labels} data-dragging={dragging || undefined}
      style={{ "--liquid-slot": `${labels === "always" ? 56 : SLOT}px`, "--liquid-tab-width": `${slot}px` } as CSSProperties}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={event => finish(event, false)} onPointerCancel={event => finish(event, true)} onLostPointerCapture={event => finish(event, true)}
      onPointerOver={onPointerOver} onPointerLeave={onPointerLeave}>
      {tabs.map((tab, index) => <TabsTrigger key={tab.value} value={tab.value} id={liquidTabId(baseId, tab.value)} aria-controls={liquidPanelId(baseId, tab.value)}
        aria-label={accessibleName(tab)} data-tab-index={index} className={styles.tab}>
        <span className={styles.icon} aria-hidden="true">{tab.icon}{tab.badge ? <span className={styles.badge} data-wide={tab.badge > 9 || undefined} aria-hidden="true">{tab.badge > 99 ? "99+" : <AnimatedCounter value={tab.badge} />}</span> : null}</span>
        <LabelSpan width={widthOf(index)}>{tab.label}</LabelSpan>
      </TabsTrigger>)}
      {/* Copie inversée DÉCORATIVE, révélée par la découpe de la lentille : aucun bouton, aucun focus, aucun événement. */}
      <motion.span className={styles.lens} aria-hidden="true" inert data-hidden={selected < 0 || undefined} data-lens="" style={{ clipPath }}>
        {tabs.map((tab, index) => <span key={tab.value} className={styles.cloneTab}>
          <span className={styles.icon}>{tab.activeIcon ?? tab.icon}{tab.badge ? <span className={styles.badge} data-wide={tab.badge > 9 || undefined}>{tab.badge > 99 ? "99+" : <AnimatedCounter value={tab.badge} />}</span> : null}</span>
          <LabelSpan width={widthOf(index)}>{tab.label}</LabelSpan>
        </span>)}
      </motion.span>
    </TabsList>
    <span ref={measureRef} className={styles.measure} aria-hidden="true">{tabs.map(tab => <span key={tab.value}>{tab.label}</span>)}</span>
  </TabsRoot></div>;
}

export default LiquidTabBar;
